import React, { useState, useEffect, useCallback } from 'react';
import {
  HeartIcon, PlusIcon, ArrowDownTrayIcon, ArrowPathIcon,
  CurrencyDollarIcon, CheckCircleIcon, ClockIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import { useAuth } from '../contexts/AuthContext';

interface Campaign {
  id: number;
  title: string;
  description?: string;
  goal_amount?: number;
  currency: string;
  campaign_type: string;
  end_date?: string;
  is_active: boolean;
  donation_count: number;
  total_raised: number;
}

interface Donation {
  id: number;
  donor_name?: string;
  donor_email?: string;
  donor_country?: string;
  amount: number;
  currency: string;
  payment_status: string;
  campaign_title?: string;
  message?: string;
  is_anonymous: boolean;
  created_at: string;
}

interface Summary {
  totals: {
    total_donations: number;
    confirmed: number;
    pending: number;
    failed: number;
    total_raised: number;
    raised_this_month: number;
  };
  byCampaign: { title: string; campaign_type: string; donation_count: number; total_raised: number }[];
  recentDonations: Donation[];
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-600',
};

const DonationsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'donations' | 'campaigns'>('overview');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [campaignForm, setCampaignForm] = useState({
    title: '', description: '', goal_amount: '', currency: 'USD',
    campaign_type: 'general', end_date: '', is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const canRefund = ['super_admin', 'finance_admin'].includes(user?.role || '');

  const token = localStorage.getItem('aaywa_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSummary = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/donations/summary`, { headers });
    if (res.ok) setSummary(await res.json());
  }, []);

  const fetchDonations = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    const res = await fetch(`${API_URL}/api/donations?${params}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setDonations(data.donations);
    }
    setLoading(false);
  }, [statusFilter]);

  const fetchCampaigns = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/donations/campaigns`, { headers });
    if (res.ok) {
      const data = await res.json();
      setCampaigns(data.campaigns);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchCampaigns();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'donations') fetchDonations();
  }, [activeTab, statusFilter]);

  const openCampaignModal = (campaign?: Campaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setCampaignForm({
        title: campaign.title,
        description: campaign.description || '',
        goal_amount: campaign.goal_amount ? String(campaign.goal_amount) : '',
        currency: campaign.currency,
        campaign_type: campaign.campaign_type,
        end_date: campaign.end_date || '',
        is_active: campaign.is_active,
      });
    } else {
      setEditingCampaign(null);
      setCampaignForm({ title: '', description: '', goal_amount: '', currency: 'USD', campaign_type: 'general', end_date: '', is_active: true });
    }
    setShowCampaignModal(true);
  };

  const saveCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...campaignForm,
      goal_amount: campaignForm.goal_amount ? Number(campaignForm.goal_amount) : null,
    };
    const url = editingCampaign
      ? `${API_URL}/api/donations/campaigns/${editingCampaign.id}`
      : `${API_URL}/api/donations/campaigns`;
    const method = editingCampaign ? 'PATCH' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    if (res.ok) { fetchCampaigns(); fetchSummary(); setShowCampaignModal(false); }
    else { const e = await res.json(); alert(e.error || 'Failed to save campaign'); }
    setSaving(false);
  };

  const refundDonation = async (donation: Donation) => {
    const reason = window.prompt(`Refund donation #${donation.id}? Add a short reason.`);
    if (reason === null) return;

    const res = await fetch(`${API_URL}/api/donations/${donation.id}/refund`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to mark donation as refunded');
      return;
    }

    fetchDonations();
    fetchSummary();
  };

  const exportCSV = () => {
    const csvHeaders = ['ID', 'Donor', 'Email', 'Country', 'Amount', 'Currency', 'Status', 'Campaign', 'Date'];
    const rows = donations.map(d => [
      d.id, d.is_anonymous ? 'Anonymous' : (d.donor_name || ''), d.donor_email || '',
      d.donor_country || '', d.amount, d.currency, d.payment_status,
      d.campaign_title || '', new Date(d.created_at).toISOString(),
    ]);
    const csv = [csvHeaders, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `donations-${Date.now()}.csv`; a.click();
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString();
  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-pink-100 p-3 rounded-xl">
            <HeartIcon className="w-8 h-8 text-pink-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AAYWA Donations</h1>
            <p className="text-gray-500 mt-0.5">Manage donation campaigns and track supporter contributions</p>
          </div>
        </div>
        <button onClick={() => openCampaignModal()}
          className="flex items-center gap-2 px-5 py-2.5 bg-pink-600 text-white rounded-xl font-semibold hover:bg-pink-700">
          <PlusIcon className="w-5 h-5" />
          New Campaign
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-gray-200">
        {(['overview', 'donations', 'campaigns'] as const).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-semibold capitalize transition-all ${activeTab === tab
              ? 'text-pink-600 border-b-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && summary && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Raised', value: formatCurrency(summary.totals.total_raised), color: 'from-pink-50 to-pink-100 border-pink-200 text-pink-900', sub: `${summary.totals.confirmed} confirmed` },
              { label: 'This Month', value: formatCurrency(summary.totals.raised_this_month), color: 'from-purple-50 to-purple-100 border-purple-200 text-purple-900', sub: '' },
              { label: 'Total Donations', value: String(summary.totals.total_donations), color: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900', sub: '' },
              { label: 'Confirmed', value: String(summary.totals.confirmed), color: 'from-green-50 to-green-100 border-green-200 text-green-900', sub: '' },
              { label: 'Pending', value: String(summary.totals.pending), color: 'from-yellow-50 to-yellow-100 border-yellow-200 text-yellow-900', sub: '' },
              { label: 'Failed', value: String(summary.totals.failed), color: 'from-red-50 to-red-100 border-red-200 text-red-900', sub: '' },
            ].map((card) => (
              <div key={card.label} className={`bg-gradient-to-br ${card.color} rounded-2xl p-5 border-2`}>
                <p className="text-xs font-semibold uppercase opacity-70">{card.label}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
                {card.sub && <p className="text-xs mt-1 opacity-60">{card.sub}</p>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Campaign */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="font-bold text-gray-900">Top Campaigns</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {summary.byCampaign.map((c) => (
                  <div key={c.title} className="px-6 py-4 flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">{c.title}</p>
                      <p className="text-xs text-gray-500 capitalize">{c.campaign_type} · {c.donation_count} donations</p>
                    </div>
                    <p className="font-bold text-pink-600">{formatCurrency(c.total_raised)}</p>
                  </div>
                ))}
                {summary.byCampaign.length === 0 && (
                  <div className="p-8 text-center text-gray-400">No campaigns yet</div>
                )}
              </div>
            </div>

            {/* Recent Donations */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="font-bold text-gray-900">Recent Donations</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {summary.recentDonations.map((d) => (
                  <div key={d.id} className="px-6 py-4 flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {d.is_anonymous ? 'Anonymous' : (d.donor_name || 'Unknown')}
                      </p>
                      <p className="text-xs text-gray-500">{d.campaign_title || 'General'} · {formatDate(d.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatCurrency(d.amount, d.currency)}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATUS_COLORS[d.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                        {d.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
                {summary.recentDonations.length === 0 && (
                  <div className="p-8 text-center text-gray-400">No donations yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Donations Tab */}
      {activeTab === 'donations' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-3">
              {['', 'confirmed', 'pending', 'failed', 'refunded'].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${statusFilter === s
                    ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800">
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center"><div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-500 mx-auto" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Donor', 'Amount', 'Campaign', 'Status', 'Country', 'Date', 'Actions'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {donations.map((d) => (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-semibold text-gray-900">
                            {d.is_anonymous ? 'Anonymous' : (d.donor_name || 'Unknown')}
                          </p>
                          {!d.is_anonymous && d.donor_email && (
                            <p className="text-xs text-gray-500">{d.donor_email}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-900">
                          {formatCurrency(d.amount, d.currency)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{d.campaign_title || 'General'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-lg text-xs font-bold ${STATUS_COLORS[d.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                            {d.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{d.donor_country || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(d.created_at)}</td>
                        <td className="px-6 py-4 text-sm">
                          {canRefund && d.payment_status === 'confirmed' ? (
                            <button
                              onClick={() => refundDonation(d)}
                              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                            >
                              Mark refunded
                            </button>
                          ) : (
                            <span className="text-xs font-semibold text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {donations.length === 0 && (
                  <div className="p-12 text-center text-gray-400">No donations found</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <span className={`px-2 py-1 rounded-lg text-xs font-bold ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-gray-400 capitalize">{c.campaign_type}</span>
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">{c.title}</h3>
              {c.description && <p className="text-sm text-gray-500 mb-4 line-clamp-2">{c.description}</p>}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Raised</span>
                  <span className="font-bold text-pink-600">{formatCurrency(c.total_raised, c.currency)}</span>
                </div>
                {c.goal_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Goal</span>
                    <span className="font-semibold text-gray-700">{formatCurrency(c.goal_amount, c.currency)}</span>
                  </div>
                )}
                {c.goal_amount && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-pink-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (c.total_raised / c.goal_amount) * 100)}%` }} />
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Donations</span>
                  <span className="font-semibold text-gray-700">{c.donation_count}</span>
                </div>
              </div>
              <button onClick={() => openCampaignModal(c)}
                className="w-full py-2 border-2 border-pink-200 text-pink-600 rounded-xl font-semibold hover:bg-pink-50 transition-colors text-sm">
                Edit Campaign
              </button>
            </div>
          ))}
          {campaigns.length === 0 && (
            <div className="col-span-3 p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">
              No campaigns yet. Create your first campaign.
            </div>
          )}
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingCampaign ? 'Edit Campaign' : 'New Campaign'}
            </h2>
            <form onSubmit={saveCampaign} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" required value={campaignForm.title}
                  onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                <textarea rows={3} value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Goal Amount</label>
                  <input type="number" min="0" value={campaignForm.goal_amount}
                    onChange={(e) => setCampaignForm({ ...campaignForm, goal_amount: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Currency</label>
                  <select value={campaignForm.currency}
                    onChange={(e) => setCampaignForm({ ...campaignForm, currency: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400">
                    {['USD', 'EUR', 'RWF', 'GBP'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Type</label>
                  <select value={campaignForm.campaign_type}
                    onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400">
                    {['general', 'training', 'traceability', 'inputs', 'cohort', 'emergency'].map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                  <input type="date" value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-pink-400" />
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={campaignForm.is_active}
                  onChange={(e) => setCampaignForm({ ...campaignForm, is_active: e.target.checked })}
                  className="w-5 h-5 text-pink-600 rounded" />
                <span className="text-sm font-semibold text-gray-700">Active (visible to donors)</span>
              </label>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowCampaignModal(false)}
                  className="px-5 py-2.5 border-2 border-gray-200 rounded-xl font-semibold text-gray-700">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-5 py-2.5 bg-pink-600 text-white rounded-xl font-semibold hover:bg-pink-700 disabled:opacity-50">
                  {saving ? 'Saving...' : (editingCampaign ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationsPage;
